import { useCallback, useMemo, createElement } from 'react';
import { useTranslation } from 'react-i18next';
import Fuse from 'fuse.js';
import { 
  FileOutlined, 
  DatabaseOutlined, 
  SettingOutlined, 
  BookOutlined,
  BranchesOutlined,
  FolderOutlined 
} from '@ant-design/icons';
import { useStore, useFlowActions, useActivePipelineId, useSelectedNodeId, useLastNodePosition } from '../store/store';
import { useSteps } from '../store/stepStore';
import { filesService, metadataService } from '../api';
import useTabManagerStore from '../store/tabManagerStore';
import { createNodeAndMaybeEdge } from '../utils/nodeCreationUtils';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('useGlobalSearch');
import { useTelemetry } from './useTelemetry';

export interface SearchResult {
  id: string;
  title: string;
  description?: string;
  category: 'files' | 'metadata' | 'steps' | 'pipelines' | 'documentation';
  type: string;
  path?: string;
  icon: React.ReactNode;
  action: () => void;
  score?: number; // Fuzzy match score (lower is better)
  relevance?: number; // Custom relevance score (higher is better)
  matchType?: 'exact' | 'prefix' | 'fuzzy' | 'contains'; // Type of match
}

export interface SearchOptions {
  categories?: string[];
  limit?: number;
  includeClientData?: boolean;
  includeServerData?: boolean;
}

// Fuzzy search configuration
const fuseOptions = {
  threshold: 0.4, // 0.0 = exact match, 1.0 = match anything
  distance: 100, // Maximum distance for fuzzy matching
  minMatchCharLength: 1,
  shouldSort: true,
  includeScore: true,
  includeMatches: true,
  keys: [
    { name: 'title', weight: 0.7 },
    { name: 'description', weight: 0.2 },
    { name: 'category', weight: 0.1 },
    { name: 'keywords', weight: 0.3 }, // For steps
  ]
};

// Helper function to calculate relevance score
const calculateRelevance = (
  result: any, 
  query: string, 
  fuseScore?: number
): { relevance: number; matchType: 'exact' | 'prefix' | 'fuzzy' | 'contains' } => {
  const title = result.title.toLowerCase();
  const queryLower = query.toLowerCase();
  
  // Exact match gets highest score
  if (title === queryLower) {
    return { relevance: 100, matchType: 'exact' };
  }
  
  // Prefix match gets high score
  if (title.startsWith(queryLower)) {
    return { relevance: 90, matchType: 'prefix' };
  }
  
  // Contains match gets medium score
  if (title.includes(queryLower)) {
    const position = title.indexOf(queryLower);
    const positionScore = Math.max(0, 80 - position * 2); // Earlier matches score higher
    return { relevance: positionScore, matchType: 'contains' };
  }
  
  // Fuzzy match score (convert Fuse score to relevance)
  if (fuseScore !== undefined) {
    const fuzzyRelevance = Math.max(0, 70 - (fuseScore * 100));
    return { relevance: fuzzyRelevance, matchType: 'fuzzy' };
  }
  
  return { relevance: 0, matchType: 'fuzzy' };
};

// Enhanced search function that combines exact, contains, and fuzzy matching
const enhancedSearch = <T extends { title: string; description?: string; category?: string; keywords?: string[] }>(
  items: T[],
  query: string,
  keys: string[] = ['title', 'description']
): Array<T & { score?: number; relevance: number; matchType: 'exact' | 'prefix' | 'fuzzy' | 'contains' }> => {
  if (!query.trim()) return [];
  
  const queryLower = query.toLowerCase();
  const results = new Map<string, T & { score?: number; relevance: number; matchType: 'exact' | 'prefix' | 'fuzzy' | 'contains' }>();
  
  // First pass: exact and contains matches
  items.forEach(item => {
    const title = item.title.toLowerCase();
    const description = item.description?.toLowerCase() || '';
    const category = item.category?.toLowerCase() || '';
    const keywords = item.keywords?.join(' ').toLowerCase() || '';
    
    // Check for matches in different fields
    const titleMatch = title.includes(queryLower);
    const descMatch = description.includes(queryLower);
    const categoryMatch = category.includes(queryLower);
    const keywordMatch = keywords.includes(queryLower);
    
    if (titleMatch || descMatch || categoryMatch || keywordMatch) {
      const { relevance, matchType } = calculateRelevance(item, query);
      const key = `${item.title}-${item.category || 'none'}`;
      
      if (!results.has(key) || results.get(key)!.relevance < relevance) {
        results.set(key, { ...item, relevance, matchType });
      }
    }
  });
  
  // Second pass: fuzzy matching for items not already found
  const unfoundItems = items.filter(item => {
    const key = `${item.title}-${item.category || 'none'}`;
    return !results.has(key);
  });
  
  if (unfoundItems.length > 0) {
    const fuse = new Fuse(unfoundItems, fuseOptions);
    const fuseResults = fuse.search(query);
    
    fuseResults.forEach(fuseResult => {
      const item = fuseResult.item;
      const key = `${item.title}-${item.category || 'none'}`;
      const { relevance, matchType } = calculateRelevance(item, query, fuseResult.score);
      
      if (relevance > 20) { // Only include fuzzy matches with decent scores
        results.set(key, { 
          ...item, 
          score: fuseResult.score, 
          relevance, 
          matchType 
        });
      }
    });
  }
  
  return Array.from(results.values()).sort((a, b) => b.relevance - a.relevance);
};

export const useGlobalSearch = () => {
  const { t } = useTranslation();
  const { trackEvent, trackUserInteraction } = useTelemetry('GlobalSearch');
  const { handleAddTab, tabs, activeTabId } = useTabManagerStore();
  const { createPipeline, setActivePipeline, addMarkdown } = useStore();
  const steps = useSteps();

  // Context awareness - get active tab type (same as PaletteSideBar)
  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const activeTabType = activeTab?.type;

  // Pipeline flow actions (same as PaletteSideBar)
  const activePipelineId = useActivePipelineId();
  const { addNode, addEdge, setSelectedNodeId } = useFlowActions();
  const lastNodePosition = useLastNodePosition();
  const currentSelectedNodeId = useSelectedNodeId();

  // Get the nodes of the currently active pipeline (same as PaletteSideBar)
  const activePipelineNodes = useStore((state) => {
    if (!activePipelineId) return [];
    const activePipeline = state.pipelines.find((p) => p.id === activePipelineId);
    return activePipeline ? activePipeline.nodes : [];
  });

  // Helper to hide welcome overlay (same as FileBrowser)
  const hideWelcomeOverlay = () => {
    window.dispatchEvent(new CustomEvent('hideWelcomeOverlay'));
  };

  // Search client-side data (already loaded) with fuzzy matching
  const searchClientData = useCallback((query: string): SearchResult[] => {
    const state = useStore.getState();
    const results: SearchResult[] = [];

    // Prepare pipeline data for fuzzy search
    const pipelineData = state.pipelines.map(pipeline => ({
      id: pipeline.id,
      title: pipeline.name,
      description: pipeline.description || t('search.openPipeline'),
      category: 'pipelines',
      type: 'pipeline'
    }));

    // Prepare markdown data for fuzzy search
    const markdownData = state.markdowns.map(markdown => ({
      id: markdown.id,
      title: markdown.name,
      description: t('search.markdownDocument'),
      category: 'documentation',
      type: 'markdown',
      content: markdown.content // Include content for searching
    }));

    // Prepare documentation pages for fuzzy search
    const documentationData = [
      {
        id: 'getting-started',
        title: t('documentation.menu.gettingStarted'),
        description: t('search.documentationPage'),
        category: 'documentation',
        type: 'doc-page',
        keywords: ['getting', 'started', 'setup', 'installation', 'basic', 'usage']
      },
      {
        id: 'tutorial',
        title: t('documentation.menu.interactiveTutorial'),
        description: t('search.documentationPage'),
        category: 'documentation',
        type: 'doc-page',
        keywords: ['tutorial', 'interactive', 'learn', 'guide', 'step', 'by', 'step']
      },
      {
        id: 'features',
        title: t('documentation.menu.features'),
        description: t('search.documentationPage'),
        category: 'documentation',
        type: 'doc-page',
        keywords: ['features', 'capabilities', 'functionality', 'what', 'can', 'do']
      },
      {
        id: 'components',
        title: t('documentation.menu.components'),
        description: t('search.documentationPage'),
        category: 'documentation',
        type: 'doc-page',
        keywords: ['components', 'api', 'interface', 'ui', 'elements', 'widgets']
      },
      {
        id: 'api',
        title: t('documentation.menu.apiReference'),
        description: t('search.documentationPage'),
        category: 'documentation',
        type: 'doc-page',
        keywords: ['api', 'reference', 'documentation', 'endpoints', 'methods', 'functions']
      }
    ];

    // Enhanced search for pipelines
    const pipelineMatches = enhancedSearch(pipelineData, query);
    pipelineMatches.forEach(match => {
      results.push({
        id: `pipeline-${match.id}`,
        title: match.title,
        description: match.description,
        category: 'pipelines',
        type: 'pipeline',
        icon: createElement(BranchesOutlined),
        score: match.score,
        relevance: match.relevance,
        matchType: match.matchType,
        action: () => {
          handleAddTab('pipeline', match.id, match.title);
          setActivePipeline(match.id);
          hideWelcomeOverlay();
        }
      });
    });

    // Enhanced search for markdown documents (include content in search)
    const markdownMatches = enhancedSearch(
      markdownData.map(md => ({
        ...md,
        searchableContent: md.content?.toLowerCase() || ''
      })), 
      query
    );
    
    markdownMatches.forEach(match => {
      // Also check content for matches
      const contentMatch = match.content?.toLowerCase().includes(query.toLowerCase());
      const finalRelevance = contentMatch ? Math.max(match.relevance, 60) : match.relevance;
      
      results.push({
        id: `markdown-${match.id}`,
        title: match.title,
        description: match.description,
        category: 'documentation',
        type: 'markdown',
        icon: createElement(BookOutlined),
        score: match.score,
        relevance: finalRelevance,
        matchType: match.matchType,
        action: () => {
          handleAddTab('markdown', match.id, match.title);
          hideWelcomeOverlay();
        }
      });
    });

    // Enhanced search for documentation pages
    const documentationMatches = enhancedSearch(documentationData, query);
    
    documentationMatches.forEach(match => {
      results.push({
        id: `doc-${match.id}`,
        title: match.title,
        description: match.description,
        category: 'documentation',
        type: 'doc-page',
        icon: createElement(BookOutlined),
        score: match.score,
        relevance: match.relevance,
        matchType: match.matchType,
        action: () => {
          logger.debug('Navigating to documentation page:', {
            pageId: match.id,
            title: match.title,
            relevance: match.relevance,
            matchType: match.matchType,
          });
          
          // Dispatch event to switch to docs view and navigate to specific page
          window.dispatchEvent(new CustomEvent('navigateToDocumentation', { 
            detail: { pageId: match.id } 
          }));
          hideWelcomeOverlay();
        }
      });
    });

    return results;
  }, [t, handleAddTab, setActivePipeline]);

  // Filter steps based on active tab type (same as PaletteSideBar)
  const filteredSteps = useMemo(() => {
    if (activeTabType === 'pipeline') {
      return steps.filter((step) => step.stepType === 'transform');
    } else if (activeTabType === 'workflow') {
      return steps.filter((step) => step.stepType === 'action');
    }
    return []; // No steps shown if not in pipeline or workflow
  }, [steps, activeTabType]);

  // Search loaded steps data (context-aware like PaletteSideBar) with fuzzy matching
  const searchStepsData = useCallback((query: string): SearchResult[] => {
    const results: SearchResult[] = [];

    // Prepare steps data for fuzzy search
    const stepData = filteredSteps.map(step => ({
      ...step,
      title: step.name,
      description: step.category,
      keywords: step.keywords || []
    }));

    // Enhanced search for steps
    const stepMatches = enhancedSearch(stepData, query);

    stepMatches.forEach(match => {
      const stepIcon = match.svg ? 
        createElement('span', { 
          dangerouslySetInnerHTML: { __html: match.svg },
          style: { display: 'inline-flex', width: '16px', height: '16px' }
        }) : 
        createElement(SettingOutlined);

      results.push({
        id: `step-${match.type}`,
        title: match.title,
        description: match.description,
        category: 'steps',
        type: match.stepType,
        icon: stepIcon,
        score: match.score,
        relevance: match.relevance,
        matchType: match.matchType,
        action: () => {
          // Add step to current pipeline (same logic as PaletteSideBar)
          if (!activePipelineId) {
            logger.warn('Cannot add step: No active pipeline/workflow', {
              activeTabType,
            });
            return;
          }

          logger.debug('Adding step to pipeline from search:', {
            stepName: match.name,
            stepType: match.stepType,
            activePipelineId,
            activeTabType,
            relevance: match.relevance,
            matchType: match.matchType,
          });

          createNodeAndMaybeEdge({
            type: match.type,
            nodeType: match.nodeType,
            name: match.name,
            icon: match.svg,
            activePipelineId,
            activePipelineNodes,
            currentSelectedNodeId: currentSelectedNodeId.nodeId,
            lastNodePosition,
            addNode,
            addEdge,
            setSelectedNodeId,
          });
        }
      });
    });

    return results;
  }, [filteredSteps, activePipelineId, activePipelineNodes, currentSelectedNodeId, lastNodePosition, addNode, addEdge, setSelectedNodeId]);

  // Search server-side data with fuzzy matching
  const searchServerData = useCallback(async (query: string): Promise<SearchResult[]> => {
    const results: SearchResult[] = [];

    try {
      // Search files with fuzzy matching
      const fileList = await filesService.getFileList();
      const allFiles: Array<{ name: string; path: string; node: any; isFolder: boolean }> = [];
      
      // Collect all files for fuzzy search
      const collectFiles = (node: any, path = ''): void => {
        const currentPath = path ? `${path}/${node.name}` : node.name;
        
        allFiles.push({
          name: node.name,
          path: currentPath,
          node,
          isFolder: node.isFolder || false
        });

        // Also log the original node structure for debugging
        if (node.isFolder) {
          logger.debug('Folder node structure:', {
            name: node.name,
            path: node.path,
            currentPath,
            keyWouldBe: node.path + node.name
          });
        }

        if (node.children) {
          node.children.forEach((child: any) => collectFiles(child, currentPath));
        }
      };

      collectFiles(fileList);

      // Prepare file data for fuzzy search
      const fileData = allFiles.map(file => ({
        title: file.name,
        description: file.path,
        category: 'files',
        ...file
      }));

      // Enhanced search for files
      const fileMatches = enhancedSearch(fileData, query);
      
      fileMatches.forEach(match => {
        const node = match.node;
        results.push({
          id: `file-${match.path}`,
          title: match.title,
          description: match.path,
          category: 'files',
          type: node.hopFileType || 'file',
          path: match.path,
          score: match.score,
          relevance: match.relevance,
          matchType: match.matchType,
          icon: node.isFolder ? createElement(FolderOutlined) : createElement(FileOutlined),
          action: async () => {
            if (node.isFolder) {
              // Navigate to file browser and expand the folder
              // The FileBrowser key format is node.path + node.name
              const folderKey = node.path + node.name;
              
              logger.debug('Navigating to folder from search:', {
                folderPath: match.path,
                folderName: node.name,
                folderKey,
                nodePath: node.path,
                relevance: match.relevance,
                matchType: match.matchType,
              });
              
              // Dispatch event to switch to files view and expand folder
              window.dispatchEvent(new CustomEvent('navigateToFolder', { 
                detail: { folderPath: folderKey } 
              }));
            } else {
              try {
                // Use match.path as the tabId (same as FileBrowser)
                const tabId = match.path;

                  let fileData: any;
                  // Use the appropriate API service method based on file type
                  if (node.hopFileType === 'PIPELINE') {
                    fileData = await filesService.getPipelineFile(tabId);
                  } else if (node.hopFileType === 'WORKFLOW') {
                    fileData = await filesService.getWorkflowFile(tabId);
                  } else if (node.hopFileType === 'MARKDOWN') {
                    fileData = await filesService.getFile(tabId);
                  }

                  logger.debug('Fetched file data from search:', fileData);

                  if (node.hopFileType === 'MARKDOWN') {
                    // Open markdown tab with content (same as FileBrowser)
                    addMarkdown(tabId, fileData.name, fileData.content);
                    handleAddTab('markdown', tabId, fileData.name, node.key);
                    hideWelcomeOverlay();
                  } else {
                    // Create pipeline/workflow with the fetched data (same structure as FileBrowser)
                    const fileType = node.hopFileType === 'WORKFLOW' ? 'workflow' : 'pipeline';
                    createPipeline(tabId, {
                      name: fileData.name,
                      description: fileData.description,
                      transforms: fileData.transforms.map((transform: any) => ({
                        id: transform.name,
                        type: transform.nodeType,
                        position: { x: transform.xloc, y: transform.yloc },
                        data: {
                          name: transform.name,
                          description: transform.description,
                          type: transform.type,
                          nodeType: transform.nodeType,
                          parallel: transform.parallel,
                        },
                      })),
                      hops: fileData.hops.map((hop: any) => ({
                        from: hop.from,
                        to: hop.to,
                        enabled: hop.enabled,
                        hopType: hop.hopType,
                      })),
                      notes: fileData.notes || [],
                    }, fileType);

                    // Open the tab and set as active (same as FileBrowser)
                    handleAddTab(node.hopFileType.toLowerCase(), tabId, node.name, node.key);
                    setActivePipeline(tabId);
                    hideWelcomeOverlay();
                  }

                logger.debug('Opened file from search:', {
                  name: node.name,
                  type: node.hopFileType,
                  tabId,
                  relevance: match.relevance,
                  matchType: match.matchType,
                });
              } catch (error) {
                logger.error('Failed to open file from search:', error);
              }
            }
          }
        });
      });

      // Search metadata with fuzzy matching
      const metadata = await metadataService.getMetadata();
      const allMetadataItems: Array<{ title: string; description: string; category: string; typeKey: string; itemKey?: string; isType: boolean }> = [];

      // Collect all metadata items for fuzzy search
      metadata.forEach(type => {
        // Add metadata types
        allMetadataItems.push({
          title: type.name,
          description: t('search.metadataType'),
          category: 'metadata',
          typeKey: type.key,
          isType: true
        });

        // Add metadata items
        type.items?.forEach(item => {
          allMetadataItems.push({
            title: item.name,
            description: `${type.name} - ${item.description || ''}`,
            category: 'metadata',
            typeKey: type.key,
            itemKey: item.key,
            isType: false
          });
        });
      });

      // Enhanced search for metadata
      const metadataMatches = enhancedSearch(allMetadataItems, query);
      
      metadataMatches.forEach(match => {
        if (match.isType) {
          results.push({
            id: `metadata-type-${match.typeKey}`,
            title: match.title,
            description: match.description,
            category: 'metadata',
            type: 'type',
            icon: createElement(DatabaseOutlined),
            score: match.score,
            relevance: match.relevance,
            matchType: match.matchType,
            action: () => {
              // Open metadata view - implement based on your routing
              logger.debug('Open metadata type:', { 
                type: match.typeKey, 
                relevance: match.relevance,
                matchType: match.matchType 
              });
            }
          });
        } else {
          results.push({
            id: `metadata-item-${match.typeKey}-${match.itemKey}`,
            title: match.title,
            description: match.description,
            category: 'metadata',
            type: 'item',
            icon: createElement(DatabaseOutlined),
            score: match.score,
            relevance: match.relevance,
            matchType: match.matchType,
            action: async () => {
              try {
                const content = await metadataService.getMetadataContent(match.typeKey, match.itemKey!);
                
                // Convert content to a formatted string if it's an object (same as MetadataView)
                let formattedContent: string;
                if (typeof content === 'object' && content !== null) {
                  formattedContent = JSON.stringify(content, null, 2);
                } else {
                  formattedContent = String(content || '# No content available');
                }
                
                // Create tabId with underscores (same format as MetadataView)
                const tabId = `metadata_${match.typeKey}_${match.itemKey}`;
                
                // First add the markdown content to the store with proper initialization (same as MetadataView)
                addMarkdown(tabId, match.title, formattedContent);
                
                // Then create the tab (same as MetadataView)
                handleAddTab('markdown', tabId, match.title);
                
                // Hide welcome overlay if it's showing
                hideWelcomeOverlay();
                
                logger.debug('Opened metadata item from search:', {
                  name: match.title,
                  type: match.typeKey,
                  itemId: match.itemKey,
                  tabId,
                  relevance: match.relevance,
                  matchType: match.matchType,
                });
              } catch (error) {
                logger.error('Failed to open metadata item from search:', error);
              }
            }
          });
        }
      });

    } catch (error) {
      logger.error('Server search failed:', error);
    }

    return results;
  }, [t, handleAddTab, createPipeline, setActivePipeline, addMarkdown]);

  // Main search function
  const search = useCallback(async (
    query: string, 
    options: SearchOptions = {}
  ): Promise<SearchResult[]> => {
    const {
      categories = ['files', 'metadata', 'steps', 'pipelines', 'documentation'],
      limit = 50,
      includeClientData = true,
      includeServerData = true
    } = options;

    if (!query.trim()) return [];

    // Track search event
    trackUserInteraction('search', 'global-search', {
      query: query.toLowerCase(), // Anonymize search terms
      queryLength: query.length,
      categories: categories.join(','),
      activeTabType,
      includeClientData,
      includeServerData
    });

    logger.debug('Global search:', { query, options, activeTabType });

    const results: SearchResult[] = [];

    // Client-side search (fast)
    if (includeClientData) {
      if (categories.includes('pipelines') || categories.includes('documentation')) {
        results.push(...searchClientData(query));
      }
      // Context-aware: Only include steps when in pipeline or workflow
      if (categories.includes('steps') && (activeTabType === 'pipeline' || activeTabType === 'workflow')) {
        results.push(...searchStepsData(query));
      }
    }

    // Server-side search (comprehensive)
    if (includeServerData && (
      categories.includes('files') || 
      categories.includes('metadata')
    )) {
      const serverResults = await searchServerData(query);
      results.push(...serverResults);
    }

    // Sort by enhanced relevance scoring and limit results
    const sortedResults = results
      .sort((a, b) => {
        // First priority: relevance score (higher is better)
        if (a.relevance !== undefined && b.relevance !== undefined) {
          if (a.relevance !== b.relevance) {
            return b.relevance - a.relevance;
          }
        }

        // Second priority: match type preference
        const matchTypePriority = { exact: 4, prefix: 3, contains: 2, fuzzy: 1 };
        const aPriority = matchTypePriority[a.matchType || 'fuzzy'];
        const bPriority = matchTypePriority[b.matchType || 'fuzzy'];
        if (aPriority !== bPriority) {
          return bPriority - aPriority;
        }

        // Third priority: fuzzy match score (lower is better for Fuse.js)
        if (a.score !== undefined && b.score !== undefined) {
          return a.score - b.score;
        }

        // Fallback: alphabetical by title
        return a.title.localeCompare(b.title);
      })
      .slice(0, limit);

    logger.debug('Enhanced search results:', { 
      count: sortedResults.length, 
      query,
      matchTypes: sortedResults.reduce((acc, r) => {
        acc[r.matchType || 'unknown'] = (acc[r.matchType || 'unknown'] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      avgRelevance: sortedResults.reduce((sum, r) => sum + (r.relevance || 0), 0) / sortedResults.length
    });

    // Track search results telemetry
    trackEvent('search-completed', {
      queryLength: query.length,
      resultCount: sortedResults.length,
      resultCategories: Object.keys(sortedResults.reduce((acc, r) => {
        acc[r.category] = true;
        return acc;
      }, {} as Record<string, boolean>)).join(','),
      hasResults: sortedResults.length > 0,
      topMatchType: sortedResults[0]?.matchType || 'none',
      avgRelevance: sortedResults.length > 0 ? 
        sortedResults.reduce((sum, r) => sum + (r.relevance || 0), 0) / sortedResults.length : 0
    });
    
    return sortedResults;
  }, [searchClientData, searchStepsData, searchServerData, activeTabType, trackEvent, trackUserInteraction]);

  return { search };
};

export default useGlobalSearch; 